import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { deepMarkLive, deepMarkNear, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A mouth stopped with rot: a ring with a dark plug. */
const MOUTH = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6.2" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="8" cy="8" r="3.4" fill="currentColor"/></svg>';
/** A root growing up to a sprout. */
const ROOT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 14.5c0-4 2-5 5-6.5s5-3 5-6.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="13" cy="2.5" r="1.8" fill="currentColor"/></svg>';
/** A drop of rot. */
const DRIP = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5c2.6 4 4.5 6.4 4.5 8.6a4.5 4.5 0 0 1-9 0C3.5 7.9 5.4 5.5 8 1.5z" fill="currentColor"/></svg>';
/** Notches: three bars. */
const NOTCH = '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="3" width="13" height="10" rx="1.5" fill="currentColor"/><path d="M5 5.5v5M8 5.5v5M11 5.5v5" stroke="#141218" stroke-width="1.5"/></svg>';
/** The Carcoma: a pale lump with roots. */
const CARCOMA = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="8.5" rx="4.6" ry="3.8" fill="currentColor"/><path d="M3.6 6.5 1 3.5M12.4 6.5 15 3.5M4 11.5l-2.5 3M12 11.5l2.5 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
/** A warning: an eye of the floor marked. */
const WARN = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.8 15 14H1z" fill="currentColor"/><path d="M8 6v4M8 11.8v.4" stroke="#141218" stroke-width="1.6" stroke-linecap="round"/></svg>';
/** The colliers' lantern of embers. */
const LANTERN = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 2.5h4M8 1v1.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><rect x="4.5" y="3.5" width="7" height="9" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M8 6c1.5 1.4 1.6 2.8 0 4.4C6.4 8.8 6.5 7.4 8 6z" fill="currentColor"/><path d="M5 14.5h6" stroke="currentColor" stroke-width="1.3"/></svg>';
/** A seed. */
const SEED = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5c3.5 2.6 4.6 6 3.4 9.2A3.6 3.6 0 0 1 4.6 10.7C3.4 7.5 4.5 4.1 8 1.5z" fill="currentColor"/><path d="M8 4v8" stroke="#141218" stroke-width="1"/></svg>';
/** A leafy bough (the Grandsire's branch). */
const BOUGH = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 14 14 2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M5 10.5c-2.6-.2-3.6-1.8-3.4-4 2.2-.2 3.6.9 3.4 4zM9 6.5c-.4-2.6.8-4.2 3-4.6.6 2.2-.4 3.8-3 4.6zM8.8 10.6c2.4-1.2 4.2-.6 5.2 1.4-2 1.2-3.8.8-5.2-1.4z" fill="currentColor"/></svg>';
/** The glyph on the button by the mark's kind. */
const GLYPH: Record<string, string> = {
  knot: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 8h13" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/><ellipse cx="8" cy="8" rx="2.2" ry="4.4" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>',
  stone: SEED,
  tangle: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 3c6 2 6 8 12 10M14 3C8 5 8 11 2 13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  patch: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.5c3 3 4.5 5.3 4.5 8a4.5 4.5 0 0 1-9 0c0-1.6.8-3 2-4 .2 1.4.8 2.2 1.8 2.6C7 6 7.2 3.8 8 1.5z" fill="currentColor"/></svg>',
  cell: ROOT,
  gate: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 14V5M6.2 14V3M9.8 14V3M13.5 14V5M1.5 8h13" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
};
/** The roots' names, in the order the sim keeps them. */
const ROOTS = ['mud', 'resin', 'hidden', 'frost'] as const;

/**
 * Inside the Grandsire on screen (docs/sendas/druida.md, chapter 5), inside a path's own plate (game/senda-hud.ts) and
 * over the board; a phone's plate kept short:
 *
 * - the buds below (`mouths`): each mouth (stopped: what it asks; cracked: wait; open: sow; clean), a tangle to cut,
 *   the drip over the hero (its seconds), and the notches' key.
 * - the Carcoma (`carcoma`): what she takes (fed: nothing; a quarter for each root cut; bare: everything, going for
 *   the door), her four roots in pips with the next one's state, what is coming (the splinters, the bite, the swipe:
 *   its seconds), a root growing back, the decision (the Grandsire's branch, the seed's cracks), the colliers at the door.
 *
 * Over the board, one button: a tap by a mark walks the hero there and offers its work by its kind (turn a knot, sow a
 * stone, cut a tangle, light a patch, root on a cell, shut Elvira's knot); the button sends it (the hero stands still
 * there, its share filling the ring). Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points
 * from the sim's own rules.
 */
export class RaicesHud {
  readonly panel: HTMLElement;
  private kind: 'mouths' | 'carcoma';
  private v = new THREE.Vector3();
  private rows: Record<string, { row: HTMLElement; text: HTMLElement }> = {};
  private pips: HTMLElement[] = [];
  private offerEl: HTMLButtonElement | null = null;
  private offerLabel: HTMLElement | null = null;
  private offerGlyph: HTMLElement | null = null;
  private offerAt = -1;
  private offerKind = '';
  private offerPoint = new THREE.Vector3();

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const r = s.rules?.senda?.deep;
    this.kind = r?.kind ?? 'mouths';
    const rows: HTMLElement[] = [];
    const row = (id: string, ico: string, label = ''): HTMLElement => {
      const text = h('span.al-wave');
      const el = h(`div.bh-row.rz-${id}`, {}, h('span.bh-ico', { html: ico }), label ? h('span.bh-label', {}, label) : '', text);
      this.rows[id] = { row: el, text };
      rows.push(el);
      return el;
    };
    if (this.kind === 'mouths') {
      (r?.mouths?.mouths ?? []).forEach((_, i) => row(`m${i}`, MOUTH, t('senda.raices.mouth', { n: i + 1 })));
      row('drip', DRIP);
      rows.push(h('div.bh-row.rz-key', {}, h('span.bh-ico', { html: NOTCH }), h('span.al-wave', {}, t('senda.raices.key'))));
    } else {
      row('takes', CARCOMA, t('senda.raices.carcoma'));
      const k = row('roots', ROOT, t('senda.raices.roots'));
      this.pips = ROOTS.map(() => h('i'));
      k.append(h('span.al-pips.rz-pips', {}, ...this.pips));
      row('next', ROOT);
      row('warn', WARN);
      row('fate', r?.carcoma?.healed ? BOUGH : SEED);
      row('door', LANTERN, t('senda.raices.colliers'));
    }
    this.panel = h(`div.bh.rz-panel.rz-${this.kind}`, {}, ...rows);
    this.buildOffer();
  }

  /** The one button over the board. */
  private buildOffer(): void {
    const label = h('small.rz-offer-label', {}, '');
    const glyph = h('span.rz-glyph', { html: GLYPH.knot ?? '' });
    const hint = t('senda.raices.hint');
    const el = h(
      'button.al-stone.rz-offer',
      { type: 'button', 'data-test': 'deep-mark', 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.offerAt >= 0 && this.mark(this.offerAt)) },
      h('span.al-ring'),
      glyph,
      label,
    ) as HTMLButtonElement;
    setStyle(el, 'display', 'none');
    this.board.append(el);
    this.offerEl = el;
    this.offerLabel = label;
    this.offerGlyph = glyph;
  }

  /** A tap on the board at a sim point: by a mark that can be worked now, its work offered; true when it was. */
  offer(s: BattleState, x: number, y: number): boolean {
    const r = s.rules?.senda?.deep;
    const w = s.senda?.deep;
    if (!r || !w || w.done >= 0) return false;
    const best = deepMarkNear(s, x, y, 1300);
    this.setOffer(s, best);
    return best >= 0;
  }

  private setOffer(s: BattleState, at: number): void {
    const r = s.rules?.senda?.deep;
    const w = s.senda?.deep;
    this.offerAt = at;
    const m = at >= 0 ? r?.marks[at] : undefined;
    if (!m || !r || !w) return;
    let x = m.x;
    let y = m.y;
    if (m.k === 'tangle') {
      const st = w.roots[m.i];
      const p = st && st.knot >= 0 ? r.nets[m.i]?.nodes[st.knot] : undefined;
      if (p) {
        x = p.x;
        y = p.y;
      }
    }
    simToWorld(x, y, this.offerPoint, 0.9);
    if (this.offerKind !== m.k) {
      this.offerKind = m.k;
      if (this.offerGlyph) this.offerGlyph.innerHTML = GLYPH[m.k] ?? '';
      this.offerEl?.setAttribute('data-kind', m.k);
    }
  }

  private set(id: string, text: string, cls?: 'warn' | 'good' | ''): void {
    const r = this.rows[id];
    if (!r) return;
    if (r.text.textContent !== text) r.text.textContent = text;
    r.row.classList.toggle('warn', cls === 'warn');
    r.row.classList.toggle('good', cls === 'good');
  }

  private hide(id: string, off: boolean): void {
    const r = this.rows[id];
    if (r && r.row.hidden !== off) r.row.hidden = off;
  }

  private secs(ticks: number): number {
    return Math.max(1, Math.ceil(ticks / TICK_HZ));
  }

  private updateMouths(s: BattleState): void {
    const r = s.rules?.senda?.deep;
    const M = r?.mouths;
    const w = s.senda?.deep;
    const hero = s.heroes.find((x) => x.downTicks === 0) ?? s.heroes[0];
    if (!r || !M || !w || !w.open.length || !hero) return;
    M.mouths.forEach((n, i) => {
      const need = (M.nodes[n] as { need: number }).need;
      const st = w.roots[i];
      if ((w.clean[i] as number) >= 0) return this.set(`m${i}`, t('senda.raices.clean'), 'good');
      if ((w.open[i] as number) >= 0) {
        if (st && st.knot >= 0) return this.set(`m${i}`, t('senda.raices.tangled'), 'warn');
        const home = st ? st.tips.filter((x) => x.mode === 3).length : 0;
        return this.set(`m${i}`, t('senda.raices.sow', { n: home }), '');
      }
      if ((w.crackUntil[n] as number) > s.tick) return this.set(`m${i}`, t('senda.raices.cracked', { s: this.secs((w.crackUntil[n] as number) - s.tick) }), 'warn');
      const got = w.buds[n];
      this.set(`m${i}`, t(got === 1 ? 'senda.raices.asksMore' : 'senda.raices.asks', { n: need }), '');
    });
    let soon = -1;
    for (const d of w.drips) {
      if (((d[0] as number) - hero.x) ** 2 + ((d[1] as number) - hero.y) ** 2 <= (M.drip.r + 200) ** 2) soon = soon < 0 ? (d[2] as number) : Math.min(soon, d[2] as number);
    }
    this.set('drip', soon >= 0 ? t('senda.raices.drip', { s: this.secs(soon - s.tick) }) : t('senda.raices.dripNone'), soon >= 0 ? 'warn' : '');
  }

  private updateCarcoma(s: BattleState): void {
    const r = s.rules?.senda?.deep;
    const C = r?.carcoma;
    const w = s.senda?.deep;
    if (!r || !C || !w || !w.cut.length) return;
    const cut = w.cut.filter(Boolean).length;
    const takes = C.takenBp[cut] ?? 10000;
    this.set('takes', cut === 0 ? t('senda.raices.fed') : cut >= 4 ? t('senda.raices.bare') : t('senda.raices.takes', { n: Math.floor(takes / 100) }), cut >= 4 ? 'good' : '');
    this.pips.forEach((p, i) => {
      const cls = w.cut[i] ? 'covered' : w.grow === i ? 'burst' : '';
      if (p.className !== cls) p.className = cls;
    });
    this.set('roots', t('senda.raices.cut', { n: cut, of: 4 }), cut >= 4 ? 'good' : '');
    // The next root standing (the nearest to cut, by its lesson), or the one growing back.
    if (w.grow >= 0) this.set('next', t('senda.raices.thread', { root: t(`senda.raices.root.${ROOTS[w.grow]}`), s: this.secs(w.growAt - s.tick) }), 'warn');
    else if (cut >= 4) this.set('next', t('senda.raices.gates', { n: w.gateUsed.filter((x) => !x).length }), '');
    else {
      const k = ROOTS.findIndex((_, i) => !w.cut[i]);
      const st = w.roots[0];
      const hint =
        k === 0 && st && st.knot >= 0
          ? t('senda.raices.mudTangled')
          : k >= 0
            ? t(`senda.raices.how.${ROOTS[k]}`)
            : '';
      this.set('next', hint, '');
    }
    const hero = s.heroes.find((x) => x.downTicks === 0) ?? s.heroes[0];
    const near = (x: number, y: number, rr: number): boolean => !!hero && (x - hero.x) ** 2 + (y - hero.y) ** 2 <= (rr + 250) ** 2;
    if (w.swipe >= 0) this.set('warn', t('senda.raices.swipe', { s: this.secs(w.swipe - s.tick) }), near(w.sx, w.sy, C.swipe.r) ? 'warn' : '');
    else if (w.bite >= 0) this.set('warn', t('senda.raices.bite', { s: this.secs(w.bite - s.tick) }), near(w.bx, w.by, C.bite.r) ? 'warn' : '');
    else if (w.cone >= 0) this.set('warn', t('senda.raices.cone', { s: this.secs(w.cone - s.tick) }), 'warn');
    else if (w.rotR) this.set('warn', t('senda.raices.rot'), 'warn');
    else this.set('warn', cut >= 4 ? t('senda.raices.toDoor') : cut >= 2 ? t('senda.raices.crawls') : t('senda.raices.spits'), '');
    if (C.healed) this.set('fate', t('senda.raices.branch'), '');
    else this.set('fate', t('senda.raices.seed', { n: Math.max(0, C.seed - w.seedHits), of: C.seed }), w.seedHits >= C.seed - 1 ? 'warn' : '');
    const colliers = (s.senda?.npcs ?? []).filter((n) => (n.id === 'ruda' || n.id.startsWith('collier')) && n.hp > 0).length;
    this.set('door', t('senda.raices.standing', { n: colliers }), colliers ? '' : 'warn');
  }

  update(s: BattleState): void {
    if (this.kind === 'mouths') this.updateMouths(s);
    else this.updateCarcoma(s);
    const w = s.senda?.deep;
    const r = s.rules?.senda?.deep;
    const el = this.offerEl;
    if (!el || !w || !r) return;
    const job = w.job;
    if (job >= 0 && job !== this.offerAt) this.setOffer(s, job);
    if (w.done >= 0 || s.result !== 'running' || (this.offerAt >= 0 && job !== this.offerAt && !deepMarkLive(s, this.offerAt))) this.offerAt = -1;
    // (A tangle's knot moves with the roots: keep it where it is now.)
    if (this.offerAt >= 0 && this.offerKind === 'tangle') this.setOffer(s, this.offerAt);
    const sent = job >= 0 && job === this.offerAt;
    el.classList.toggle('sent', sent);
    const m = this.offerAt >= 0 ? r.marks[this.offerAt] : undefined;
    const need = !m ? 24 : m.k === 'stone' || m.k === 'patch' ? (r.mouths?.throw ?? r.carcoma?.resin.throw ?? 12) : m.k === 'tangle' ? (r.mouths?.knot ?? r.carcoma?.knot ?? 48) : m.k === 'gate' ? (r.carcoma?.touch ?? 12) : (r.mouths?.cut ?? r.carcoma?.cut ?? 24);
    el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, need)).toFixed(2) : '0');
    const shut = !!m && m.k === 'knot' && w.closed[m.i] === 1;
    const word = !m ? '' : m.k === 'knot' ? (shut ? 'open' : 'shut') : m.k;
    const label = sent ? (w.held > 0 ? t('senda.raices.working') : t('senda.raices.going')) : word ? t(`senda.raices.do.${word}`) : '';
    if (this.offerLabel && this.offerLabel.textContent !== label) this.offerLabel.textContent = label;
    el.classList.toggle('shut', shut);
    el.disabled = s.result !== 'running';
    this.hide('drip', false);
  }

  /** Every frame: the offer over its place. */
  place(camera: THREE.Camera, w: number, ht: number): void {
    const el = this.offerEl;
    if (!el) return;
    if (this.offerAt < 0) {
      setStyle(el, 'display', 'none');
      return;
    }
    this.v.copy(this.offerPoint).project(camera);
    if (this.v.z > 1 || this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1) {
      setStyle(el, 'display', 'none');
      return;
    }
    setStyle(el, 'display', '');
    pinMark(el, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht);
  }
}
